import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Mail, Lock, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import AuthLayout from "@/components/AuthLayout";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from || "/";
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      await login(email, password);
      toast.success("Signed in");
      navigate(from, { replace: true });
    } catch (err) {
      const msg = formatApiError(err);
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout
      step="[ auth_01 ] sign in"
      title="Access your account"
      subtitle="Enter your credentials to continue. New here? Create an account below."
    >
      <form onSubmit={submit} className="space-y-5" data-testid="login-form">
        <div className="space-y-2">
          <label className="mono text-[10px] uppercase tracking-[0.24em] text-neutral-500 flex items-center gap-2">
            <Mail size={11} /> email
          </label>
          <Input
            type="email"
            required
            autoFocus
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
            className="rounded-none bg-[#0a0a0a] border-neutral-800 focus-visible:border-[#E2FF54] focus-visible:ring-0 mono h-12 text-white"
            data-testid="login-email"
          />
        </div>
        <div className="space-y-2">
          <label className="mono text-[10px] uppercase tracking-[0.24em] text-neutral-500 flex items-center gap-2">
            <Lock size={11} /> password
          </label>
          <Input
            type="password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="••••••••"
            className="rounded-none bg-[#0a0a0a] border-neutral-800 focus-visible:border-[#E2FF54] focus-visible:ring-0 mono h-12 text-white"
            data-testid="login-password"
          />
        </div>

        {error && (
          <div
            className="border border-[#FF3B30]/40 bg-[#FF3B30]/[0.06] p-3 mono text-xs text-[#FF9994]"
            data-testid="login-error"
          >
            {error}
          </div>
        )}

        <Button
          type="submit"
          disabled={loading}
          className="btn-brutal w-full rounded-none bg-[#E2FF54] text-black hover:bg-[#d4f531] mono uppercase tracking-[0.24em] text-xs py-6"
          data-testid="login-submit"
        >
          {loading ? (
            <>
              <Loader2 size={14} className="animate-spin mr-2" /> signing in…
            </>
          ) : (
            <>
              sign in <ArrowRight size={14} className="ml-2" />
            </>
          )}
        </Button>
      </form>

      <div className="mt-8 pt-6 border-t border-neutral-900 mono text-xs text-neutral-500 flex items-center justify-between">
        <span>no account?</span>
        <Link
          to="/signup"
          className="text-[#E2FF54] hover:underline uppercase tracking-[0.2em] text-[11px]"
          data-testid="login-goto-signup"
        >
          create one →
        </Link>
      </div>
    </AuthLayout>
  );
}
